/**
 * ドメインモデルの定義。
 *
 * すべて zod スキーマとして定義し、そこから TypeScript 型を導出する。
 * これにより「API の入力検証」と「型定義」が常に一致し、
 * バックエンド・フロントエンド・ネイティブアプリで同じ定義を共有できる。
 */
import { z } from "zod";
import { CURRENCY_CODES } from "./money.js";
/**
 * DB が発行する ID (cuid) の型。
 * 上限が無いと巨大な文字列をそのまま検索まで通してしまうので、余裕を見て切る。
 */
export const idSchema = z.string().trim().min(1).max(40);
/**
 * 金額の上限。
 *
 * DB の金額列は Postgres の integer (int4) なので、2,147,483,647 を超えると
 * 書き込み時に落ちる。zod で止めないと 400 ではなく 500 になってしまう。
 * 最小通貨単位なので、円なら約 21 億円、ドルなら約 2,147 万ドルまで扱える。
 */
export const MAX_AMOUNT = 2_147_483_647;
/** 支出額など「1 以上」の金額 */
export const positiveAmountSchema = z.number().int().positive().max(MAX_AMOUNT);
/** 立替や負担など「0 でもよい」金額 */
export const nonNegativeAmountSchema = z.number().int().nonnegative().max(MAX_AMOUNT);
/**
 * 1 グループのメンバー数の上限。
 *
 * 精算ソルバーは人数が増えるほど重くなる (14 人までは厳密解、それ以上は近似)。
 * 際限なく増やせると、1 回の計算で応答時間を持っていかれるため上限を置く。
 */
export const MAX_GROUP_MEMBERS = 50;
/** money.ts の通貨定義から自動的に導出する (定義の二重管理を避ける) */
export const currencyCodeSchema = z.enum(CURRENCY_CODES);
/** 支出のカテゴリ。入力候補やアイコン表示に使う */
export const expenseCategorySchema = z.enum([
    "food", // 食事・飲み会
    "transport", // 交通
    "stay", // 宿泊
    "leisure", // 遊び・レジャー
    "shopping", // 買い物
    "utility", // 生活費・光熱費
    "gift", // プレゼント・お祝い
    "other", // その他
]);
export const EXPENSE_CATEGORY_LABELS = {
    food: "食事",
    transport: "交通",
    stay: "宿泊",
    leisure: "レジャー",
    shopping: "買い物",
    utility: "生活費",
    gift: "ギフト",
    other: "その他",
};
export const EXPENSE_CATEGORY_ICON = {
    food: "utensils",
    transport: "train",
    stay: "hotel",
    leisure: "ferris-wheel",
    shopping: "shopping-bag",
    utility: "lightbulb",
    gift: "gift",
    other: "receipt",
};
export const memberSchema = z.object({
    id: idSchema,
    name: z.string().min(1).max(40),
    /** ログイン済みユーザーと紐づいている場合の ID */
    userId: z.string().nullable().optional(),
});
/** 立替 (誰がいくら払ったか)。1つの支出に複数人の立替を許す */
export const payerShareSchema = z.object({
    memberId: idSchema,
    amount: nonNegativeAmountSchema,
});
/**
 * 負担ルール。
 * - equal      : 対象メンバーで均等割り (端数は決定的に配分)
 * - shares     : 比率 (例: 大人2 : 子ども1)
 * - percentage : パーセント指定 (合計 100)
 * - exact      : 金額を直接指定 (合計が支出額と一致すること)
 * - adjusted   : 一部の人だけ金額を固定し、残りを均等割り
 *                (例: お酒を飲まなかった人だけ 2,000 円にして、残りは均等)
 */
export const splitRuleSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("equal"),
        memberIds: z.array(idSchema).min(1).max(MAX_GROUP_MEMBERS),
    }),
    z.object({
        type: z.literal("shares"),
        entries: z
            .array(z.object({ memberId: idSchema, share: z.number().positive().max(MAX_AMOUNT) }))
            .min(1)
            .max(MAX_GROUP_MEMBERS),
    }),
    z.object({
        type: z.literal("percentage"),
        entries: z
            .array(z.object({ memberId: idSchema, percent: z.number().nonnegative().max(100) }))
            .min(1)
            .max(MAX_GROUP_MEMBERS),
    }),
    z.object({
        type: z.literal("exact"),
        entries: z
            .array(z.object({ memberId: idSchema, amount: nonNegativeAmountSchema }))
            .min(1)
            .max(MAX_GROUP_MEMBERS),
    }),
    z.object({
        type: z.literal("adjusted"),
        entries: z
            .array(z.object({
            memberId: idSchema,
            /** null = 金額を固定せず、残額を均等割りする対象 */
            amount: nonNegativeAmountSchema.nullable(),
        }))
            .min(1)
            .max(MAX_GROUP_MEMBERS),
    }),
]);
export const expenseSchema = z.object({
    id: idSchema,
    title: z.string().min(1).max(80),
    category: expenseCategorySchema,
    /** 支出総額 (最小通貨単位) */
    amount: positiveAmountSchema,
    payers: z.array(payerShareSchema).min(1),
    split: splitRuleSchema,
    /** 発生日 (YYYY-MM-DD)。null = 未指定 */
    occurredOn: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    note: z.string().max(500).nullable().optional(),
});
/**
 * 精算の制約。スプリの差別化機能である「誰が誰に払うか」の指定。
 *
 * 効き方が違う 3 つだけに絞ってある。
 * 「夫婦」「同居人」のような関係の名前は持たない — 名前が違っても
 * 計算が同じなら、選ぶ意味が無いため。
 *
 * - prefer / avoid … 送金の相手を変える (負担額は変わらない)
 * - cover          … 負担そのものを付け替える (A が B の負担を肩代わりする)
 */
export const constraintKindSchema = z.enum([
    "prefer", // この2人の間の送金を優先する
    "avoid", // この2人の間の送金を避ける (第三者を経由する)
    "cover", // memberIdA が memberIdB の負担を肩代わりする (向きがある)
]);
export const CONSTRAINT_LABELS = {
    prefer: "2人の間の送金を優先する",
    avoid: "2人の間の送金を避ける",
    cover: "1人目が2人目の金額を負担する",
};
/** 向きに意味がある種類 (A と B を入れ替えると意味が変わる) */
export const DIRECTED_CONSTRAINT_KINDS = ["cover"];
export const constraintSchema = z.object({
    id: idSchema,
    /** cover では「負担する人」。prefer / avoid では順不同 */
    memberIdA: idSchema,
    /** cover では「負担してもらう人」 */
    memberIdB: idSchema,
    kind: constraintKindSchema,
});
/** イベント (グループ) 全体。共有 URL の shareToken はここには持たない (DTO 側で足す) */
export const groupSchema = z.object({
    id: idSchema,
    name: z.string().min(1).max(60),
    currency: currencyCodeSchema,
    members: z.array(memberSchema),
    constraints: z.array(constraintSchema),
    expenses: z.array(expenseSchema),
});
//# sourceMappingURL=types.js.map